import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompassHeading } from '../../hooks/useCompassHeading';

/**
 * 🇰🇷 NaverMapAdapter - 한국 전용 지도 (네이버 공식 SDK)
 * 
 * index.html에 선언된 정적 스크립트를 사용하며,
 * 로드 상태를 정밀하게 진단합니다.
 */
const NaverMapAdapter = ({
    center,
    spots = [],
    selectedSpotId,
    onSpotClick,
    myPos,
    isExpanded,
    mapTarget,
    setOffScreenIndicator,
    initialViewState,
    onViewChange,
    distanceRadius, // 🎯 [NEW] Zoom based on distance
    pointSpots = [], // 🎯 [NEW]
    onPointSpotClick, // 🎯 [NEW]
    onMapReady, // 🎯 [NEW] 지도 인스턴스 콜백 (외부에서 map 접근용)
    isTreasureHuntMode // 🎯 [FIX] 보물찾기 모드 여부
}) => {
    const { t } = useTranslation();
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const myMarkerRef = useRef(null);
    const pointSpotMarkersRef = useRef(new Map()); // 🎯 [NEW] Point Spot markers (Map<id, marker>)
    const prevIsTreasureHuntMode = useRef(false); // 🎯 [FIX] 모드 변경 감지
    const prevHeadingRef = useRef(undefined); // 🎯 [NEW] 방향 평활화(Smoothing)용

    const [sdkLoaded, setSdkLoaded] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [isTracking, setIsTracking] = useState(true); // 🎯 [Tracking Mode] Default: Follow User
    const prevMapTarget = useRef(null);
    const prevDistanceRadius = useRef(null); // 🎯 [FIX] Start null to force initial sync

    // 🎯 [FIX] Refs for callbacks to prevent stale closures in event listeners
    const onSpotClickRef = useRef(onSpotClick);
    const onPointSpotClickRef = useRef(onPointSpotClick);

    // 🎯 [FIX] Sync Refs with Props
    useEffect(() => {
        onSpotClickRef.current = onSpotClick;
        onPointSpotClickRef.current = onPointSpotClick;
    }, [onSpotClick, onPointSpotClick]);

    // 🎯 SDK 로드 상태 체크 (index.html 정적 로드 대기)
    useEffect(() => {
        // 1. 이미 로드 완료된 경우
        if (window.naver && window.naver.maps) {
            setSdkLoaded(true);
            return;
        }

        console.log('[NaverMap] SDK 로드 대기 중...');

        const checkInterval = setInterval(() => {
            if (window.naver && window.naver.maps) {
                console.log('[NaverMap] SDK 로드 감지됨!');
                setSdkLoaded(true);
                clearInterval(checkInterval);
            } else {
                // 디버깅용 로그 (개발자 도구 콘솔 확인용)
                // 너무 자주 찍히지 않도록 개발 단계에서만 확인
                // console.log('[NaverMap] window.naver:', !!window.naver);
            }
        }, 500);

        // 느린 연결에서 SDK를 비동기로 받아도 지도 진입을 기다릴 수 있게 한다.
        const timeout = setTimeout(() => {
            if (!window.naver || !window.naver.maps) {
                console.error('[NaverMap] SDK Init Timeout. window.naver:', window.naver);

                // 원인 추정
                let reasonKey = 'map_naver_object_missing';
                if (window.naver && !window.naver.maps) {
                    reasonKey = 'map_naver_maps_missing';
                } else if (!window.naver) {
                    reasonKey = 'map_naver_script_missing';
                }

                setLoadError({
                    titleKey: 'map_load_failed',
                    messageKey: reasonKey,
                    detailKey: 'map_check_console'
                });
                clearInterval(checkInterval);
            }
        }, 15000);

        return () => {
            clearInterval(checkInterval);
            clearTimeout(timeout);
        };
    }, []);

    // 🎯 [Helper] Radius -> Zoom Level
    const getZoomLevel = (radius) => {
        if (!radius) return 13;
        if (radius <= 1000) return 14;       // 1km - closer view
        if (radius <= 5000) return 12;       // 5km
        if (radius <= 10000) return 11;      // 10km
        if (radius <= 50000) return 9;       // 50km - wider view
        return 8;
    };

    // 🎯 지도 초기화
    useEffect(() => {
        if (!sdkLoaded || !mapContainerRef.current || mapRef.current || loadError) return;

        const targetCenter = (mapTarget && typeof mapTarget === 'object' && !Array.isArray(mapTarget) && Number.isFinite(mapTarget.lat))
            ? { lat: mapTarget.lat, lng: mapTarget.lng }
            : null;
        if (isTreasureHuntMode && !center && !myPos && !targetCenter) return;
        const mapCenter = (initialViewState?.center && initialViewState?.center.lat)
            ? initialViewState.center
            : (center || myPos || targetCenter || { lat: 37.5665, lng: 126.9780 });

        console.log('[NaverMap] 지도 초기화 시도, 좌표:', mapCenter);

        try {
            // 🎯 [Fix] Calculate initial zoom immediately to prevent "snap" effect
            // PRIORITY: mapTarget.zoom > initialViewState.zoom > distanceRadius
            let initialZoom = 14;

            // mapTarget check
            const targetZoom = (mapTarget && typeof mapTarget === 'object' && !Array.isArray(mapTarget)) ? mapTarget.zoom : null;

            if (targetZoom) {
                initialZoom = targetZoom;
                console.log('[NaverMap] Initializing with explicit mapTarget zoom:', initialZoom);
            } else if (initialViewState?.zoom) {
                initialZoom = initialViewState.zoom;
            } else {
                initialZoom = getZoomLevel(distanceRadius);
            }

            const mapInstance = new window.naver.maps.Map(mapContainerRef.current, {
                center: new window.naver.maps.LatLng(mapCenter.lat, mapCenter.lng),
                zoom: initialZoom,
                zoomControl: true,
                zoomControlOptions: {
                    position: window.naver.maps.Position.RIGHT_CENTER
                }
            });

            mapRef.current = mapInstance;

            // 🎯 [ViewState] Event Listener
            const updateView = () => {
                if (onViewChange && mapRef.current) {
                    const c = mapRef.current.getCenter();
                    const z = mapRef.current.getZoom();
                    onViewChange({ center: { lat: c.y, lng: c.x }, zoom: z });
                }
            };

            // 'idle' event fires after pan/zoom ends
            window.naver.maps.Event.addListener(mapInstance, 'idle', updateView);

            // 🎯 [Tracking Mode] State for "Free Mode" vs "Follow Mode"
            // map.setTrackingMode is exposed for external control
            mapInstance.setTrackingMode = (enabled) => {
                // console.log('[NaverMap] Tracking Mode:', enabled);
                setIsTracking(enabled);
            };

            // 🎯 [Interaction] User Drag -> Disable Tracking (Free Mode)
            window.naver.maps.Event.addListener(mapInstance, 'dragstart', () => {
                setIsTracking(false);
            });

            // 🎯 [Initial]
            if (onMapReady) onMapReady(mapInstance);

            setLoadError(null);
            setLoadError(null);
        } catch (err) {
            console.error('[NaverMap] Init Error:', err);
            setLoadError({
                titleKey: 'map_init_error',
                message: err.message,
                detailKey: 'map_refresh_please'
            });
        }

        return () => {
            // 🎯 [Cleanup] Clear marker ref to prevent zombie markers
            if (myMarkerRef.current) {
                myMarkerRef.current.setMap(null);
                myMarkerRef.current = null;
            }
        };
    }, [sdkLoaded, center, initialViewState, onMapReady]); // onMapReady added to deps

    // 🎯 맵 리사이즈
    useEffect(() => {
        if (!mapRef.current || loadError) return;
        try {
            setTimeout(() => {
                window.naver?.maps?.Event?.trigger(mapRef.current, 'resize');
            }, 300);
        } catch (err) { }
    }, [isExpanded, loadError]);

    // 🎯 중심 이동 (External Target) + Zoom
    // This takes HIGHEST priority - if zoom is specified, it overrides everything
    useEffect(() => {
        if (!mapRef.current || !mapTarget || loadError) return;

        console.log('[NaverMap] mapTarget changed:', mapTarget);

        // Support both array [lat, lng, timestamp] and object { lat, lng, zoom, timestamp }
        const isObject = typeof mapTarget === 'object' && !Array.isArray(mapTarget);
        const lat = isObject ? mapTarget.lat : mapTarget[0];
        const lng = isObject ? mapTarget.lng : mapTarget[1];
        const timestamp = isObject ? mapTarget.timestamp : mapTarget[2];
        const zoom = isObject ? mapTarget.zoom : null;

        if (!lat || !lng) return;

        // Create unique key - if no timestamp, use current time to force update
        const targetKey = `${lat}-${lng}-${timestamp || Date.now()}`;
        if (prevMapTarget.current === targetKey) return;
        prevMapTarget.current = targetKey;

        try {
            console.log('[NaverMap] Applying mapTarget:', { lat, lng, zoom });
            const newCenter = new window.naver.maps.LatLng(lat, lng);

            // Apply zoom if specified - this is EXPLICIT and takes priority
            if (zoom !== null && zoom !== undefined) {
                console.log('[NaverMap] Setting explicit zoom:', zoom);
                // Use morph for combined pan & zoom (smoother and more reliable than separate calls)
                mapRef.current.morph(newCenter, zoom);

                // Mark that we've applied explicit zoom to prevent distanceRadius override
                prevDistanceRadius.current = null; // Reset to allow distanceRadius to work later
            } else {
                mapRef.current.panTo(newCenter);
            }
        } catch (err) {
            console.error('[NaverMap] 중심 이동 오류:', err);
        }
    }, [mapTarget, loadError]);

    // 🎯 [NEW] Distance Radius -> Zoom Level (Centered on User)
    // BUT: Don't override if mapTarget has explicit zoom (e.g., from treasure hunt mode)
    useEffect(() => {
        if (!mapRef.current || !distanceRadius || !myPos || loadError) return;
        if (distanceRadius === prevDistanceRadius.current) return;

        // Check if mapTarget has explicit zoom - if so, don't override
        const isObject = typeof mapTarget === 'object' && !Array.isArray(mapTarget);
        const hasExplicitZoom = isObject && mapTarget?.zoom !== null && mapTarget?.zoom !== undefined;

        if (hasExplicitZoom) {
            console.log('[NaverMap] Skipping distanceRadius zoom - mapTarget has explicit zoom');
            return;
        }

        try {
            // Center on user's position with new zoom
            const newCenter = new window.naver.maps.LatLng(myPos.lat, myPos.lng);
            const z = getZoomLevel(distanceRadius); // Use helper

            mapRef.current.setCenter(newCenter);
            mapRef.current.setZoom(z);
            prevDistanceRadius.current = distanceRadius;
        } catch (err) {
            console.error('[NaverMap] Zoom 변경 오류:', err);
        }
    }, [distanceRadius, myPos, loadError, mapTarget]);

    // 🎯 [FIX] 보물찾기 모드 종료 시 줌 레벨 복구
    // 보물찾기 모드(높은 줌)에서 일반 모드(거리 기반 줌)로 돌아올 때 강제 업데이트
    useEffect(() => {
        if (!isTreasureHuntMode && prevIsTreasureHuntMode.current) {
            console.log('[NaverMap] Treasure Hunt Mode Exited - Restoring Zoom');
            // 강제로 줌 복구 로직 실행
            if (mapRef.current && distanceRadius && myPos) {
                // 거리 반경에 맞는 줌으로 즉시 리셋
                // (mapTarget이 해제된 직후 실행되어야 함)
                try {
                    const z = getZoomLevel(distanceRadius);
                    console.log(`[NaverMap] Restoring zoom to ${z} for radius ${distanceRadius}m`);
                    mapRef.current.setZoom(z);

                    // 중심도 내 위치로 재조정 (선택 사항이나 UX상 좋음)
                    const newCenter = new window.naver.maps.LatLng(myPos.lat, myPos.lng);
                    mapRef.current.panTo(newCenter);

                    prevDistanceRadius.current = distanceRadius; // Sync state
                } catch (e) {
                    console.error('[NaverMap] Zoom restore failed:', e);
                }
            }
        }
        prevIsTreasureHuntMode.current = isTreasureHuntMode;
    }, [isTreasureHuntMode, distanceRadius, myPos]);

    // 🎯 [NEW] 스팟 선택 시 자동 패닝
    const lastPannedId = useRef(null); // 🎯 [FIX] Prevent snapping back when spots update

    useEffect(() => {
        if (!mapRef.current || !selectedSpotId || !spots.length) return;

        // 이미 해당 스팟으로 이동했다면, spots 데이터가 갱신되어도 다시 이동하지 않음
        if (lastPannedId.current === selectedSpotId) return;

        const spot = spots.find(s => s.id === selectedSpotId);
        if (spot && spot.geo) {
            console.log('[NaverMap] Panning to Spot:', spot.title);
            const center = new window.naver.maps.LatLng(spot.geo.lat, spot.geo.lng);
            mapRef.current.panTo(center, { duration: 400 });
            lastPannedId.current = selectedSpotId; // 이동 완료 기록
        }
    }, [selectedSpotId, spots]);

    // 🎯 [NEW] Off-screen Indicator Logic
    const prevIndicator = useRef({ angle: null, distance: null }); // 🎯 [FIX] Prevent excessive updates

    useEffect(() => {
        if (!mapRef.current || !myPos || !setOffScreenIndicator) return;

        const handleIndicator = () => {
            try {
                const map = mapRef.current;
                const bounds = map.getBounds();
                const myLatLng = new window.naver.maps.LatLng(myPos.lat, myPos.lng);

                let newState = { angle: null, distance: null };

                if (!bounds.hasLatLng(myLatLng)) {
                    // 화면 중심과 내 위치 사이의 각도 계산
                    const center = map.getCenter();
                    const projection = map.getProjection();

                    const centerPoint = projection.fromCoordToOffset(center);
                    const myPoint = projection.fromCoordToOffset(myLatLng);

                    const dx = myPoint.x - centerPoint.x;
                    const dy = myPoint.y - centerPoint.y;
                    const angle = Math.atan2(dy, dx) * (180 / Math.PI);

                    // 거리 계산 (미터)
                    const dist = projection.getDistance(center, myLatLng);
                    const distStr = dist < 1000 ? `${Math.round(dist)}m` : `${(dist / 1000).toFixed(1)}km`;

                    newState = {
                        angle: angle + 90, // 보정 (CSS 회전 기준)
                        distance: distStr
                    };
                }

                // 🎯 [FIX] Only update if changed significantly
                const prev = prevIndicator.current;
                const isDifferent = prev.distance !== newState.distance || Math.abs((prev.angle || 0) - (newState.angle || 0)) > 1;

                if (isDifferent) {
                    prevIndicator.current = newState;
                    setOffScreenIndicator(newState);
                }
            } catch (err) { }
        };

        const listeners = [
            window.naver.maps.Event.addListener(mapRef.current, 'idle', handleIndicator),
            window.naver.maps.Event.addListener(mapRef.current, 'center_changed', handleIndicator), // Throttled update needed?
            window.naver.maps.Event.addListener(mapRef.current, 'zoom_changed', handleIndicator)
        ];

        handleIndicator(); // 초기 실행

        return () => {
            listeners.forEach(l => window.naver.maps.Event.removeListener(l));
        };
    }, [myPos, setOffScreenIndicator, sdkLoaded]);





    // ... (inside component)

    // 🎯 [Heading] New Robust Compass Hook
    const compassHeading = useCompassHeading();
    const isCompassActive = compassHeading !== 0; // Simple check

    // Removed old verify logic


    // ... (Existing SDK Load Check)

    // ... (Existing Map Init)

    // ... (Existing Map Resize)

    // ... (Existing Map Target)

    // ... (Existing Zoom Logic)

    // ... (Existing Zoom Restore)

    // ... (Existing Manual Pan)

    // ... (Existing OffScreen Indicator)

    // 🎯 내 위치 마커 (Heading 화살표 포함)
    useEffect(() => {
        if (!mapRef.current || !sdkLoaded || !myPos || loadError) return;

        try {
            const myPosLatLng = new window.naver.maps.LatLng(myPos.lat, myPos.lng);

            // 🎯 [Tracking Logic] Only Pan if Tracking is ON
            if (isTracking) {
                mapRef.current.panTo(myPosLatLng, { duration: 300, easing: 'easeOutCubic' });
            }

            // 🎯 [Heading Logic] Calculate Heading FIRST
            const speed = myPos.speed || 0;
            const useGpsHeading = speed > 0.5 && myPos.heading !== undefined;
            let displayHeading = 0;

            if (useGpsHeading) {
                const rawHeading = myPos.heading;
                if (prevHeadingRef.current === undefined) prevHeadingRef.current = rawHeading;
                let diff = rawHeading - prevHeadingRef.current;
                if (diff > 180) diff -= 360;
                if (diff < -180) diff += 360;
                displayHeading = prevHeadingRef.current + (diff * 0.25);
                prevHeadingRef.current = (displayHeading + 360) % 360;
            } else {
                if (isCompassActive) {
                    displayHeading = compassHeading;
                } else {
                    displayHeading = prevHeadingRef.current || 0;
                }
                prevHeadingRef.current = compassHeading;
            }
            const cssHeading = displayHeading;

            // 🎯 [Marker Icon Content]
            const getMarkerContent = (heading) => `
                <div style="position: relative; width: 40px; height: 40px; pointer-events: none;">
                    <div style="
                        position: absolute;
                        top: 50%; left: 50%;
                        width: 0; height: 0;
                        border-left: 6px solid transparent;
                        border-right: 6px solid transparent;
                        border-bottom: 10px solid #3B82F6;
                        transform: translate(-50%, -15px) rotate(${heading}deg);
                        transform-origin: center calc(100% + 5px);
                        filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
                        z-index: 1;
                        transition: transform 0.2s linear;
                    "></div>
                    <div style="
                        position: absolute;
                        top: 50%; left: 50%;
                        transform: translate(-50%, -50%);
                        width: 24px; height: 24px;
                        background: rgba(79, 70, 229, 0.3);
                        border-radius: 50%;
                        animation: pulseRing 2s infinite ease-out;
                    "></div>
                    <div style="
                        position: absolute;
                        top: 50%; left: 50%;
                        transform: translate(-50%, -50%);
                        width: 16px; height: 16px;
                        background: #4F46E5;
                        border: 3px solid white;
                        border-radius: 50%;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                        z-index: 2;
                    "></div>
                    <style>
                        @keyframes pulseRing {
                            0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
                            100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
                        }
                    </style>
                </div>
            `;

            // 🎯 [Marker Creation/Update]
            if (myMarkerRef.current) {
                myMarkerRef.current.setPosition(myPosLatLng);
                // 🎯 [Fix] Update Icon to reflect new Heading
                myMarkerRef.current.setIcon({
                    content: getMarkerContent(cssHeading),
                    anchor: new window.naver.maps.Point(20, 20)
                });
            } else {
                myMarkerRef.current = new window.naver.maps.Marker({
                    position: myPosLatLng,
                    map: mapRef.current,
                    icon: {
                        content: getMarkerContent(cssHeading),
                        anchor: new window.naver.maps.Point(20, 20)
                    },
                    title: t('map_my_location'),
                    zIndex: 100, // 🎯 [Fix] Physically below Gift Boxes (300) to ensure clickability
                    clickable: false // 🎯 [Fix] Allow clicks to pass through to Gift Boxes below
                });
            }
        } catch (err) { }
    }, [myPos, sdkLoaded, loadError, compassHeading, isTracking, t]);

    // 🎯 스팟 마커
    useEffect(() => {
        if (!mapRef.current || !sdkLoaded || loadError) return;

        try {
            markersRef.current.forEach(m => m.setMap(null));
            markersRef.current = [];

            const validSpots = spots.filter(s => s.geo?.lat && s.geo?.lng);

            validSpots.forEach(spot => {
                const isSelected = spot.id === selectedSpotId;
                const isDormant = spot.status === 'dormant';

                // 🎯 [UX 개선] 3D Floating Colors
                const COLORS = {
                    landmark: { gradient: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 60%, #2563EB 100%)', solid: '#3B82F6' },
                    store: { gradient: 'linear-gradient(135deg, #F87171 0%, #EF4444 60%, #DC2626 100%)', solid: '#EF4444' },
                    footprint: { gradient: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 60%, #D97706 100%)', solid: '#F59E0B' }
                };

                const colorType = spot.type === 'store' ? COLORS.store : (spot.type === 'footprint' ? COLORS.footprint : COLORS.landmark);

                const marker = new window.naver.maps.Marker({
                    position: new window.naver.maps.LatLng(spot.geo.lat, spot.geo.lng),
                    map: mapRef.current,
                    icon: {
                        // ... (Original Content)
                        content: `
                            <div style="
                                position: relative;
                                display: flex;
                                flexDirection: column;
                                align-items: center;
                                width: 26px; height: 40px;
                            ">
                                ${isSelected ? `
                                    <div style="
                                        position: absolute;
                                        top: 14px; left: 50%;
                                        transform: translate(-50%, -50%);
                                        width: 24px; height: 24px;
                                        background: rgba(255, 68, 68, 0.3);
                                        border-radius: 50%;
                                        animation: pulseRing 2s infinite ease-out;
                                    "></div>
                                    <style>
                                        @keyframes pulseRing {
                                            0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
                                            100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
                                        }
                                    </style>
                                ` : ''}
                                
                                <div style="
                                    position: relative;
                                    display: flex;
                                    flex-direction: column;
                                    align-items: center;
                                    z-index: 2;
                                ">
                                    <div style="
                                        width: ${isSelected ? '28px' : '20px'};
                                        height: ${isSelected ? '28px' : '20px'};
                                        background: ${colorType.gradient};
                                        opacity: ${isDormant ? 0.6 : 1};
                                        border: ${isSelected ? '3px solid #FF4444' : '1.5px solid #FFFFFF'};
                                        border-radius: 50%;
                                        box-shadow: 
                                            inset 0 2px 3px rgba(255,255,255,0.4),
                                            inset 0 -2px 2px rgba(0,0,0,0.1),
                                            ${isSelected ? '0 0 12px rgba(255, 68, 68, 0.6),' : ''}
                                            0 2px 5px rgba(0,0,0,0.25);
                                        transform: ${isSelected ? 'scale(1.05)' : 'scale(1)'};
                                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                    ">
                                        <svg viewBox="0 0 24 24" width="12" height="12" fill="#FFFFFF" style="filter: drop-shadow(0 1px 1px rgba(0,0,0,0.1));">
                                            ${spot.type === 'store'
                                ? '<path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>'
                                : spot.type === 'footprint'
                                    ? '<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>'
                                    : '<path d="M12 2L2 7v2h20V7L12 2zm0 2.5L18 7H6l6-2.5zM4 11v9h3v-6h2v6h2v-6h2v6h2v-6h2v6h3v-9H4z"/>'}
                                        </svg>
                                    </div>

                                    <div style="
                                        width: 0;
                                        height: 0;
                                        border-left: 5px solid transparent;
                                        border-right: 5px solid transparent;
                                        border-top: 7px solid #FFFFFF;
                                        margin-top: -1px;
                                        position: relative;
                                        z-index: -1;
                                        opacity: ${isDormant ? 0.6 : 1};
                                        filter: drop-shadow(0 1px 1px rgba(0,0,0,0.1));
                                    ">
                                        <div style="
                                            position: absolute;
                                            top: -8px;
                                            left: -3px;
                                            width: 0;
                                            height: 0;
                                            border-left: 3px solid transparent;
                                            border-right: 3px solid transparent;
                                            border-top: 6px solid ${colorType.solid};
                                        "></div>
                                    </div>
                                </div>
                                
                                <div style="
                                    position: absolute;
                                    bottom: 4px;
                                    width: ${isSelected ? '20px' : '14px'};
                                    height: 5px;
                                    background: rgba(0,0,0,0.3);
                                    border-radius: 50%;
                                    filter: blur(1px);
                                    z-index: 1;
                                    opacity: ${isDormant ? 0.3 : 0.6};
                                "></div>
                            </div>
                        `,
                        anchor: new window.naver.maps.Point(13, 38)
                    },
                    title: spot.title
                });

                window.naver.maps.Event.addListener(marker, 'click', () => {
                    // 🎯 [FIX] Use Ref to call latest callback
                    onSpotClickRef.current && onSpotClickRef.current(spot);
                });

                markersRef.current.push(marker);
            });
        } catch (err) {
            console.error('[NaverMap] 마커 렌더링 오류:', err);
        }
    }, [spots, selectedSpotId, sdkLoaded, loadError]); // Removed callback deps

    // 🎯 [NEW] Point Spot Markers (Gift Box)
    useEffect(() => {
        if (!mapRef.current || !sdkLoaded || loadError) return;

        const currentMarkers = pointSpotMarkersRef.current; // Map<id, marker>
        const newSpotIds = new Set(pointSpots.map(s => s.id));

        // 1. Remove markers
        currentMarkers.forEach((marker, id) => {
            if (!newSpotIds.has(id)) {
                marker.setMap(null);
                currentMarkers.delete(id);
            }
        });

        // 2. Add new markers
        pointSpots.forEach((spot) => {
            if (!currentMarkers.has(spot.id)) {
                // ... (Original Logic)
                const isLandmark = spot.isLandmarkPointSpot;
                const glowColor = isLandmark ? 'rgba(96, 165, 250, 0.5)' : 'rgba(168, 85, 247, 0.5)';
                const shadowColor = isLandmark ? 'rgba(96, 165, 250, 0.4)' : 'rgba(168, 85, 247, 0.4)';
                const boxStyle = isLandmark
                    ? 'font-size: 20px; line-height: 20px; position: relative; z-index: 2; filter: hue-rotate(180deg) saturate(0.9) brightness(1.05);'
                    : 'font-size: 20px; line-height: 20px; position: relative; z-index: 2;';

                const marker = new window.naver.maps.Marker({
                    position: new window.naver.maps.LatLng(spot.lat, spot.lng),
                    map: mapRef.current,
                    zIndex: 300,
                    icon: {
                        content: `
                            <div style="
                                position: relative;
                                width: ${isLandmark ? '34px' : '32px'};
                                height: ${isLandmark ? '34px' : '32px'};
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                cursor: pointer;
                                box-shadow: 0 4px 8px ${shadowColor};
                                border-radius: 50%;
                                animation: giftFloat 1.5s ease-in-out infinite;
                            ">
                                <div style="
                                    position: absolute;
                                    width: 40px;
                                    height: 40px;
                                    top: -4px;
                                    left: -4px;
                                    border-radius: 50%;
                                    background: radial-gradient(circle, ${glowColor} 0%, transparent 70%);
                                    filter: blur(4px);
                                    animation: glowPulse 2s ease-in-out infinite;
                                    z-index: 1;
                                "></div>
                                <div style="${boxStyle}">🎁</div>
                                <div style="position: absolute; top: -6px; right: -2px; font-size: 14px; z-index: 3; animation: sparkle 1.2s ease-in-out infinite;">✨</div>
                                <div style="position: absolute; bottom: -2px; left: -6px; font-size: 10px; z-index: 3; animation: sparkle 1.5s ease-in-out infinite 0.5s;">✨</div>
                                <style>
                                    @keyframes giftFloat { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-5px) scale(1.05); } }
                                    @keyframes sparkle { 0%, 100% { opacity: 0; transform: scale(0.5) rotate(0deg); } 50% { opacity: 1; transform: scale(1.2) rotate(15deg); } }
                                    @keyframes glowPulse { 0%, 100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.2); } }
                                </style>
                            </div>
                        `,
                        anchor: new window.naver.maps.Point(16, 16)
                    },
                    title: spot.title
                });

                window.naver.maps.Event.addListener(marker, 'click', () => {
                    // 🎯 [FIX] Use Ref to call latest callback
                    onPointSpotClickRef.current && onPointSpotClickRef.current(spot);
                });

                currentMarkers.set(spot.id, marker);
            }
        });

        // Cleanup moved to component unmount
    }, [pointSpots, sdkLoaded, loadError]); // Removed callback deps

    // 🎯 에러 발생 시 UI
    if (loadError) {
        // API 키 누락 체크
        if (!process.env.REACT_APP_NAVER_MAP_CLIENT_ID) {
            return (
                <div style={errorStyle}>
                    <div style={{ fontSize: "2rem" }}>⚠️</div>
                    <div>{t('map_api_key_missing')}</div>
                </div>
            )
        }

        return (
            <div style={errorStyle}>
                <div style={{ fontSize: "2rem", marginBottom: "8px" }}>⚠️</div>
                <div style={{ fontWeight: "bold", fontSize: "1rem" }}>{t(loadError.titleKey)}</div>
                <div style={{ margin: "10px 0" }}>{loadError.messageKey ? t(loadError.messageKey) : loadError.message}</div>
                <div style={{ fontSize: "0.8rem", color: "#666" }}>{t(loadError.detailKey)}</div>
                <button
                    onClick={() => window.location.reload()}
                    style={reloadButtonStyle}
                >
                    {t('map_refresh')}
                </button>
            </div>
        );
    }

    if (!sdkLoaded) {
        return (
            <div style={{
                width: "100%", height: "100%",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "#f0f0f0", color: "#666"
            }}>
                {t('map_naver_loading')}
            </div>
        );
    }

    return <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />;
};

const errorStyle = {
    width: "100%", height: "100%",
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    background: "#f8f9fa", color: "#495057",
    padding: "20px", textAlign: "center", border: "1px solid #dee2e6"
};

const reloadButtonStyle = {
    marginTop: "15px", padding: "8px 16px",
    background: "#212529", color: "white",
    border: "none", borderRadius: "4px", cursor: "pointer"
};

// ... (memoization wrapper at export)
export default React.memo(NaverMapAdapter);
